import useTranslation from '../useTranslation';
import { useEffect, useRef, useState } from 'react';

import useBatteries from 'hooks/useBatteries';

import { BATTERIES_PAGE_SIZE } from './constants';
import { batteryMocks } from './mockData';
import type {
    BatteryResponse,
    BatteryView,
    HandleSearch,
    PaginatedGenerator,
    UseData
} from './types';

const useData = (): UseData => {
    const { NO_BATTERIES, NO_MORE_BATTERIES } = useTranslation();
    const { fetch, isLoading: isFetching, reset } = useBatteries();

    const [batteryData, setBatteryData] = useState<BatteryResponse[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const generatorRef = useRef<PaginatedGenerator<BatteryResponse> | null>(null);
    const generationRef = useRef(0);

    const fetchData = async (searchTerm?: string) => {
        reset();

        const generation = ++generationRef.current;

        generatorRef.current = fetch({
            pageSize: BATTERIES_PAGE_SIZE,
            searchQuery: searchTerm || undefined
        });

        setBatteryData([]);

        const { done, value } = await generatorRef.current.next();

        if (generation !== generationRef.current) {
            return;
        }

        setBatteryData(value ?? []);
        setHasNextPage(!done);
        setInitialized(true);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchNext = async () => {
        if (!generatorRef.current || !hasNextPage) {
            return;
        }

        const generation = generationRef.current;

        const { done, value } = await generatorRef.current.next();

        if (generation !== generationRef.current) {
            return;
        }

        if (value) {
            setBatteryData(current => [...current, ...value]);
        }

        setHasNextPage(!done);
    };

    const handleSearch: HandleSearch = async searchValue => {
        await fetchData(searchValue);
    };

    const { variants } = batteryMocks;

    const batteries: BatteryView[] = batteryData.map((battery, index) => {
        const variant = variants[index % variants.length];

        return {
            cyclesTotal: variant.cyclesTotal,
            cyclesUsed: variant.cyclesUsed,
            dronesUsedWith: variant.dronesUsedWith,
            id: battery.id,
            isSyncStale: variant.isSyncStale,
            lastSynced: variant.lastSynced,
            name: battery.name
        };
    });

    const isLoading = isFetching || !initialized;

    const endingMessage = batteries.length === 0 ? NO_BATTERIES : NO_MORE_BATTERIES;

    return {
        batteries,
        endingMessage,
        fetchNext,
        hasNextPage,
        isLoading,
        onSearch: handleSearch
    };
};

export default useData;
